/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1792540800000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Oct 21 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "amazon-ads",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Amazon Ads",
          "depth": 0,
          "id": "connect-to-amazon-ads"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Segment",
          "depth": 1,
          "id": "segment"
        }, {
          "value": "Events V2",
          "depth": 1,
          "id": "events-v2"
        }, {
          "value": "Events (deprecated)",
          "depth": 1,
          "id": "events-deprecated"
        }, {
          "value": "Handling PII and hashing",
          "depth": 0,
          "id": "handling-pii-and-hashing"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Matched users count",
          "depth": 1,
          "id": "matched-users-count"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      a: "a",
      strong: "strong",
      p: "p",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3",
      h4: "h4",
      img: "img",
      ol: "ol",
      pre: "pre"
    }, _provideComponents(), props.components), {ArrayFanOutTip, EventSyncWarning, MatchRatesExplanation, CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!ArrayFanOutTip) _missingMdxReference("ArrayFanOutTip", true);
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!MatchRatesExplanation) _missingMdxReference("MatchRatesExplanation", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported sync modes"
            }), _jsx(_components.th, {
              children: "API reference"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#segment",
                children: _jsx(_components.strong, {
                  children: "Segment"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync first-party and third-party data from any source to Amazon Ads DSP and AMC audiences"
            }), _jsx(_components.td, {
              children: "Add"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://advertising.amazon.com/API/docs/en-us/ads-data-manager",
                children: "Manage audiences ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#events-v2",
                children: _jsx(_components.strong, {
                  children: "Events V2"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data from any source to Amazon Ads as conversion events"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://advertising.amazon.com/API/docs/en-us/guides/conversions-api/overview",
                children: "Send conversion events ↗"
              })
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "#events-deprecated",
                children: _jsx(_components.strong, {
                  children: "Events (deprecated)"
                })
              })
            }), _jsx(_components.td, {
              children: "Sync data to Amazon Ads DSP as offline conversion events using Amazon's legacy events endpoint"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://advertising.amazon.com/API/docs/en-us/dsp-conversion-builder",
                children: "Upload conversion events ↗"
              })
            })]
          })]
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-amazon-ads",
        children: "Connect to Amazon Ads"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations?from=docs",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and select ", _jsx(_components.strong, {
          children: "Add destination"
        }), ". Select ", _jsx(_components.strong, {
          children: "Amazon Ads"
        }), " and select ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to ", _jsx(_components.strong, {
          children: "Amazon Ads"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["For the ", _jsx(_components.strong, {
          children: "Authentication method"
        }), ", select ", _jsx(_components.strong, {
          children: "Log in to Amazon Ads"
        }), " and log in to your Amazon Ads account. After you authenticate, Hightouch redirects you back to complete setup with these fields:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Region"
          }), " — The Amazon API region that covers your marketplaces."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ad account ID"
          }), " — Your advertiser account ID."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Manager account ID"
          }), " — Required to sync to Ads Data Manager audiences. To find it, select your manager account name in the top-right corner of the ", _jsx(_components.a, {
            href: "https://advertising.amazon.com/home",
            children: "Amazon Ads console"
          }), "; the console labels it ", _jsx(_components.strong, {
            children: "Partner ID"
          }), ". It begins with ", _jsx(_components.code, {
            children: "amzn1.ads1.ma1"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Amazon Ads destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs?from=docs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and select ", _jsx(_components.strong, {
          children: "Add sync"
        }), ". Then, select the relevant model and the Amazon Ads destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "segment",
        children: "Segment"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to Amazon Ads audiences."
      }), "\n", _jsx(_components.h4, {
        id: "choose-an-audience-type",
        children: "Choose an audience type"
      }), "\n", _jsx(_components.p, {
        children: "You can sync to first-party data or third-party data based on the type of audience."
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "First-party data"
          }), " — Sync hashed user data to audiences."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Third-party data (DSP)"
          }), " — Sync external identifiers such as MAIDs and cookie IDs."]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "choose-an-audience-system-first-party-data",
        children: "Choose an audience system (first-party data)"
      }), "\n", _jsxs(_components.p, {
        children: ["Amazon deprecated its legacy AMC Advertiser Audience API in favor of ", _jsx(_components.a, {
          href: "https://advertising.amazon.com/API/docs/en-us/ads-data-manager",
          children: "Ads Data Manager"
        }), ". For first-party data audiences, choose which system the sync uses:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Ads Data Manager (recommended)"
          }), " — Amazon's current audience system. Hightouch creates an audience dataset, shares it to Amazon DSP, and syncs members into it."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Legacy AMC audience"
          }), " — Use this only to sync to an audience created under the legacy API. Legacy audience IDs are numeric."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-amazon-ads-audience-system.png",
          alt: "Amazon audience system selection in the sync configuration"
        })
      }), "\n", _jsx(_components.p, {
        children: "The choice is fixed once the sync creates its audience. Audiences created under one system can't be written to through the other."
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Existing first-party audience syncs continue to run on the legacy API without\nany changes. To move an audience to Ads Data Manager, create a new sync using\nthe Ads Data Manager option, then point your DSP campaigns at the new\naudience."
        })
      }), "\n", _jsx(_components.h4, {
        id: "set-up-ads-data-manager-audiences",
        children: "Set up Ads Data Manager audiences"
      }), "\n", _jsx(_components.p, {
        children: "Before creating an Ads Data Manager sync:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Your Amazon Ads account must accept the Ads Data Manager terms of service. Amazon prompts for acceptance the first time someone opens Ads Data Manager (", _jsx(_components.strong, {
            children: "Data & events > Data manager"
          }), ") in the ", _jsx(_components.a, {
            href: "https://advertising.amazon.com/home",
            children: "Amazon Ads console"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["The destination's ", _jsx(_components.strong, {
            children: "Manager account ID"
          }), " field must be set. See ", _jsx(_components.a, {
            href: "#connect-to-amazon-ads",
            children: "Connect to Amazon Ads"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "When creating the sync, you can create a new audience or select an existing Ads Data Manager dataset from the dropdown. For new audiences, configure:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Marketplace"
          }), " — The Amazon marketplace the audience is created in. The audience is shared to Amazon DSP in this marketplace."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Country code"
          }), " — The ISO 3166-1 alpha-2 country code for the members in the audience."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Retain hashed identifiers"
          }), " — Optional. When enabled, Amazon retains hashed data for 90 days and refreshes identity tokens."]
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Ads Data Manager dataset names only allow letters, numbers, hyphens, and\nunderscores. Hightouch automatically replaces other characters in your\naudience name with underscores when creating the dataset. The audience name\nshown in Amazon DSP is unchanged."
        })
      }), "\n", _jsx(_components.p, {
        children: "Audience expiration (TTL), audience fees, and connection IDs are features of the legacy API and don't apply to Ads Data Manager audiences."
      }), "\n", _jsx(_components.h4, {
        id: "configure-record-matching",
        children: "Configure record matching"
      }), "\n", _jsx(_components.p, {
        children: "To match rows from your model to an audience in Amazon Ads, select a model column and corresponding Amazon Ads field. For third-party data audiences, you can match on any of the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Cookie ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Mobile Advertising ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "External ID"
          })
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "For first-party data audiences, you can match on any of the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Phone"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "City"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "State"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "First name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Last name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Address"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "External ID"
          })
        }), "\n"]
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Ads Data Manager audiences require an ", _jsx(_components.strong, {
            children: "External ID"
          }), " for every member.\nHightouch rejects rows without one. The ", _jsx(_components.strong, {
            children: "External ID"
          }), " must be an opaque\nidentifier such as a user ID from your database. Amazon rejects the entire\nbatch if the External ID contains personal data such as an email address.\nInclude at least one strong identifier (email or phone) so Amazon can resolve\nmembers."]
        })
      }), "\n", _jsx(ArrayFanOutTip, {
        destinationName: "Amazon Ads"
      }), "\n", _jsx(_components.h3, {
        id: "events-v2",
        children: "Events V2"
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to Amazon Ads as conversion events using Amazon's current events API."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "configure-event-name-and-timestamp",
        children: "Configure event name and timestamp"
      }), "\n", _jsx(_components.p, {
        children: "Select the model columns that provide the event name and the event timestamp."
      }), "\n", _jsx(_components.h4, {
        id: "configure-record-matching-1",
        children: "Configure record matching"
      }), "\n", _jsx(_components.p, {
        children: "Select the model columns to use as match keys. You can use any of the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Phone"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "First name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Last name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Address"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "City"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "State"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Postal"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "MAID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Ramp ID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Match ID"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "map-fields",
        children: "Map fields"
      }), "\n", _jsxs(_components.p, {
        children: ["Map your model columns to Amazon Ads event fields. You can also map up to 13 custom data fields with a type of ", _jsx(_components.strong, {
          children: "String"
        }), ", ", _jsx(_components.strong, {
          children: "Integer"
        }), ", or ", _jsx(_components.strong, {
          children: "Timestamp"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Under ", _jsx(_components.strong, {
          children: "Advanced"
        }), ", you can lower the rows per batch. The default and maximum is 500."]
      }), "\n", _jsx(_components.h3, {
        id: "events-deprecated",
        children: "Events (deprecated)"
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Amazon deprecated the legacy events endpoint in favor of the ", _jsx(_components.a, {
            href: "#events-v2",
            children: "newer events\nAPI"
          }), ". Use ", _jsx(_components.strong, {
            children: "Events V2"
          }), " for new syncs."]
        })
      }), "\n", _jsx(_components.p, {
        children: "Sync data from any source to Amazon Ads as offline conversion events using Amazon's legacy events endpoint."
      }), "\n", _jsx(_components.h4, {
        id: "create-a-conversion-definition-in-amazon-ads",
        children: "Create a conversion definition in Amazon Ads"
      }), "\n", _jsxs(_components.p, {
        children: ["Before syncing events with the legacy endpoint, you need to create a conversion definition in Amazon Ads.\nSee the ", _jsx(_components.a, {
          href: "https://advertising.amazon.com/API/docs/en-us/dsp-conversion-builder",
          children: "Amazon Ads API documentation"
        }), " for more information.\nYou can create a conversion definition using the Amazon Ads UI or API."]
      }), "\n", _jsxs(_components.p, {
        children: ["To create a conversion definition using the Amazon Ads API, use the endpoint documented ", _jsx(_components.a, {
          href: "https://advertising.amazon.com/API/docs/en-us/dsp-conversion-builder#tag/Amazon-Conversion-Definitions/operation/dspAmazonCreateConversionDefinitions",
          children: "here"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "To create a conversion definition using the Amazon Ads UI, follow these steps:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "https://advertising.amazon.com/en-us/sign-in",
            children: "Sign in"
          }), " to Amazon Ads."]
        }), "\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Data & events > Events"
          }), " and choose your advertiser account when prompted."]
        }), "\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.strong, {
            children: "Conversions"
          }), " tab and select ", _jsx(_components.strong, {
            children: "Add conversion"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "After creating a conversion definition, you can select it in the sync configuration."
      }), "\n", _jsx(_components.h4, {
        id: "configure-record-matching-2",
        children: "Configure record matching"
      }), "\n", _jsx(_components.p, {
        children: "For events, you can use any of the following fields as identifiers in Amazon Ads:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Phone"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "City"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "State"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Address"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Postal"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "MAID"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "First name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Last name"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["See the ", _jsx(_components.a, {
          href: "https://advertising.amazon.com/API/docs/en-us/dsp-conversion-builder",
          children: "Amazon Ads API documentation"
        }), " for more information on required fields for event syncing."]
      }), "\n", _jsx(_components.h4, {
        id: "map-fields-1",
        children: "Map fields"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch lets you sync event properties via field mapping."
      }), "\n", _jsx(_components.h2, {
        id: "handling-pii-and-hashing",
        children: "Handling PII and hashing"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch normalizes and hashes first-party user data fields with SHA-256 before sending them to Amazon Ads."
      }), "\n", _jsx(_components.p, {
        children: "You can turn off automatic hashing in the sync configuration. When you do, you're responsible for normalizing and hashing data according to Amazon Ads' requirements before it reaches Hightouch."
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["For Hightouch platform error codes related to Amazon Ads, see ", _jsx(_components.a, {
            href: "/developer-tools/error-codes#other-destinations",
            children: "Error codes:\nAmazon Ads"
          }), "."]
        })
      }), "\n", _jsx(_components.h3, {
        id: "matched-users-count",
        children: "Matched users count"
      }), "\n", _jsx(_components.p, {
        children: "Below only applies to the Segment sync type."
      }), "\n", _jsx(MatchRatesExplanation, {
        destination: "Amazon Ads"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h4, {
        id: "legacy-audience-ids-are-numeric",
        children: "Legacy audience IDs are numeric"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "Legacy AMC audience IDs are numeric. To sync to an Ads Data Manager dataset, select Ads Data Manager as the audience system instead.\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " An Ads Data Manager dataset name or ID was entered in the legacy audience ID field. Legacy AMC audience IDs are always numeric."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Select ", _jsx(_components.strong, {
          children: "Ads Data Manager"
        }), " as the audience system and pick the dataset from the dropdown instead of entering it manually."]
      }), "\n", _jsx(_components.h4, {
        id: "manager-account-id-is-missing",
        children: "Manager account ID is missing"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "Ads Data Manager requires your Amazon manager account ID. Add it in this destination's settings (find it in the Amazon Ads console under Ads Data Manager).\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " The sync uses Ads Data Manager, but the destination has no manager account ID configured."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Select your manager account name in the top-right corner of the ", _jsx(_components.a, {
          href: "https://advertising.amazon.com/home",
          children: "Amazon Ads console"
        }), " to find the ID; the console labels it ", _jsx(_components.strong, {
          children: "Partner ID"
        }), ". It begins with ", _jsx(_components.code, {
          children: "amzn1.ads1.ma1"
        }), ". Add it to the destination's ", _jsx(_components.strong, {
          children: "Manager account ID"
        }), " field."]
      }), "\n", _jsx(_components.h4, {
        id: "ads-data-manager-terms-not-accepted",
        children: "Ads Data Manager terms not accepted"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "This Amazon Ads account has not accepted the Ads Data Manager terms and conditions. Accept them in the Amazon Ads console (Ads Data Manager), then re-run this sync.\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Your Amazon Ads account hasn't accepted the Ads Data Manager terms of service."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Open Ads Data Manager (", _jsx(_components.strong, {
          children: "Data & events > Data manager"
        }), ") in the ", _jsx(_components.a, {
          href: "https://advertising.amazon.com/home",
          children: "Amazon Ads console"
        }), " and accept the terms when prompted, then re-run the sync."]
      }), "\n", _jsx(_components.h4, {
        id: "all-records-in-this-request-are-invalid",
        children: "All records in this request are invalid"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "All records in this request are invalid. Fix the errors before resubmitting the request.\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " Amazon rejected every member in the batch. The most common cause is an ", _jsx(_components.strong, {
          children: "External ID"
        }), " mapping that contains personal data such as an email address. It can also occur when members lack a strong identifier."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Map ", _jsx(_components.strong, {
          children: "External ID"
        }), " to an opaque identifier such as a user ID, and include an email or phone identifier in the record matching configuration."]
      }), "\n", _jsx(_components.h4, {
        id: "dataset-name-already-exists",
        children: "Dataset name already exists"
      }), "\n", _jsx(_components.pre, {
        children: _jsx(_components.code, {
          className: "hljs language-text",
          children: "An Ads Data Manager dataset named \"...\" already exists in this Amazon account and was not created by this sync. Rename this audience or remove the existing dataset in Ads Data Manager.\n"
        })
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Cause:"
        }), " A dataset with the sync's audience name already exists in your Ads Data Manager account, and it wasn't created by this sync."]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.strong, {
          children: "Solution:"
        }), " Give the audience a different name in the sync configuration, or remove the conflicting dataset in Ads Data Manager."]
      }), "\n", _jsx(_components.h4, {
        id: "invalid-conversion-definition-id",
        children: "Invalid conversion definition ID"
      }), "\n", _jsx(_components.p, {
        children: "If you receive an error that the conversion definition ID is invalid on a legacy event sync, ensure that you have created a conversion definition in Amazon Ads and selected it in the sync configuration."
      }), "\n", _jsx(_components.p, {
        children: "If you are using a column for the conversion definition ID, ensure that the column values match the numeric ID of the conversion definition in Amazon Ads, rather than its name."
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
